import { Suspense, lazy, useMemo, useState } from "react";
import {
  emptyHubMap,
  getHubMapLayerCounts,
  getSourceLabel,
  isClickableMapCodePoint,
  MAP_LAYER_LABELS,
  MAP_SOURCE_COLORS,
} from "../../../../utils/projectHubMapUtils";
import {
  CoordsAccordion,
  CoordsTable,
  CoordsTableTitle,
  CoordsTableWrap,
  LayerCount,
  LayerDot,
  LayerToggle,
  LayerToggles,
  MapCodeButton,
  MapLoading,
  MapSectionRoot,
  MapSummary,
} from "./styles";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip, Polygon, useMap } from "react-leaflet";

const ProjectHubInteractiveMap = lazy(() =>
  import("./ProjectHubInteractiveMap")
);

const DEFAULT_VISIBILITY = {
  reference: true,
  alerts: true,
  requests: true,
  firmsDetections: true,
  firmsAreas: true,
};

const ProjectHubMapSection = ({
  map: mapProp,
  geographicCoordinates = [],
  onPointCodeClick,
  loadingCode = null,
}) => {
  const map = mapProp || emptyHubMap();
  const counts = getHubMapLayerCounts(map);
  const [visibility, setVisibility] = useState(DEFAULT_VISIBILITY);

  const points = Array.isArray(map.all_points) ? map.all_points : [];
  const coords = Array.isArray(geographicCoordinates)
    ? geographicCoordinates
    : [];

  const legendItems = useMemo(
    () => [
      {
        key: "reference",
        label: MAP_LAYER_LABELS.reference,
        color: MAP_SOURCE_COLORS.geographic_coordinate,
        count: counts.reference,
        hint: "Pontos de geographic_coordinates + EMA — não é o endereço do projeto",
      },
      {
        key: "alerts",
        label: MAP_LAYER_LABELS.alerts,
        color: MAP_SOURCE_COLORS.alert,
        count: counts.alerts,
      },
      {
        key: "requests",
        label: MAP_LAYER_LABELS.requests,
        color: MAP_SOURCE_COLORS.alert_request_firms,
        count: counts.requests,
        hint: "Focos FIRMS vinculados a solicitações de alerta",
      },
      {
        key: "firmsDetections",
        label: MAP_LAYER_LABELS.firmsDetections,
        color: MAP_SOURCE_COLORS.firms_scan,
        count: counts.firmsDetections,
        hint: "Focos dos scans FIRMS (raw_detections_json)",
      },
      {
        key: "firmsAreas",
        label: MAP_LAYER_LABELS.firmsAreas,
        color: MAP_SOURCE_COLORS.firms_scan,
        count: counts.firmsAreas,
      },
    ],
    [counts]
  );

  const wktToLatLngs = (wkt) => {
  if (!wkt) return null;
  const rings = [...wkt.matchAll(/\(([^()]+)\)/g)].map((m) =>
    m[1]
      .split(",")
      .map((pair) => pair.trim().split(/\s+/).map(Number))
      .filter(([lon, lat]) => Number.isFinite(lat) && Number.isFinite(lon))
      .map(([lon, lat]) => [lat, lon])
  );
  return rings.length ? rings : null;
};

  const toggleLayer = (key) => {
    setVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderCodeCell = (point) => {
    const label = point.label || "—";
    if (!isClickableMapCodePoint(point) || typeof onPointCodeClick !== "function") {
      return label;
    }

    const code = String(point.label).trim();
    const busy = loadingCode != null && String(loadingCode) === code;

    return (
      <MapCodeButton
        type="button"
        title={
          point.source === "alert"
            ? "Abrir alerta"
            : "Abrir solicitação de alerta"
        }
        disabled={Boolean(loadingCode)}
        onClick={() => onPointCodeClick(point)}
      >
        {busy ? "Abrindo…" : code}
      </MapCodeButton>
    );
  };

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  return (
    <MapSectionRoot>
      <MapSummary title="Totais por camada do mapa (hub)">
        {plural(counts.allPoints, "ponto no mapa", "pontos no mapa")}
        {" · "}
        {plural(
          counts.reference,
          "coordenada cadastrada",
          "coordenadas cadastradas"
        )}
        {" · "}
        {plural(counts.alerts, "alerta", "alertas")}
        {" · "}
        {plural(counts.requests, "solicitação", "solicitações")}
        {" · "}
        {plural(counts.firmsDetections, "detecção FIRMS", "detecções FIRMS")}
        {" · "}
        {plural(counts.firmsAreas, "área FIRMS", "áreas FIRMS")}
      </MapSummary>

      <LayerToggles>
        {legendItems.map((item) => (
          <LayerToggle key={item.key} title={item.hint || item.label}>
            <input
              type="checkbox"
              checked={Boolean(visibility[item.key])}
              onChange={() => toggleLayer(item.key)}
            />
            <LayerDot $color={item.color} />
            <span>{item.label}</span>
            <LayerCount>{item.count}</LayerCount>
          </LayerToggle>
        ))}
      </LayerToggles>

      <Suspense fallback={<MapLoading>Carregando mapa…</MapLoading>}>
        <ProjectHubInteractiveMap
          referencePoints={map.reference_points || []}
          alertPoints={map.alert_points || []}
          alertRequestPoints={map.alert_request_points || []}
          firmsDetectionPoints={map.firms_detection_points || []}
          firmsLayers={map.firms_layers || []}
          visibility={visibility}
        />
      </Suspense>

      {coords.length > 0 || points.length > 0 ? (
        <CoordsAccordion>
          <summary>Tabela de coordenadas</summary>
          {coords.length > 0 ? (
            <CoordsTableWrap>
              <CoordsTableTitle>Coordenadas cadastradas</CoordsTableTitle>
              <CoordsTable>
                <thead>
                  <tr>
                    <th>Descrição</th>
                    <th>Coordenadas</th>
                  </tr>
                </thead>
                <tbody>
                  {coords.map((coord) => (
                    <tr key={coord.id}>
                      <td>{coord.descricao || "—"}</td>
                      <td>
                        {coord.latitude}, {coord.longitude}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CoordsTable>
            </CoordsTableWrap>
          ) : null}

          {points.length > 0 ? (
            <CoordsTableWrap>
              <CoordsTableTitle>Todos os pontos do mapa</CoordsTableTitle>
              <CoordsTable>
                <thead>
                  <tr>
                    <th>Origem</th>
                    <th>Código</th>
                    <th>Coordenadas</th>
                  </tr>
                </thead>
                <tbody>
                  {points.map((point, index) => (
                    <tr
                      key={`${point.lat}-${point.lon}-${point.source}-${index}`}
                    >
                      <td>{getSourceLabel(point.source)}</td>
                      <td>{renderCodeCell(point)}</td>
                      <td>
                        {point.lat}, {point.lon}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CoordsTable>
            </CoordsTableWrap>
          ) : null}
        </CoordsAccordion>
      ) : null}
    </MapSectionRoot>
  );
};

export default ProjectHubMapSection;
